import { useMemo, useState } from "react";
import { Link, type To } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { ApiError } from "@shared-core/data/api/errors";
import type { CustomerSpendReport } from "@shared-core/data/customer-api-types";
import {
  formatSpendDate,
  shapeChart,
  SPEND_PERIODS,
  spendPeriodFromDate,
  spendReportCsv,
  type SpendPeriodId,
} from "@shared-core/domain/customerSpend";
import { currentTime } from "@shared-core/domain/current-time";
import { usePortalViewer } from "./PortalViewerContext";
import { PortalScopeNote } from "./PortalScopeNote";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { AlertTriangle, Download, Lock } from "lucide-react";
import { MonthlyBarChart } from "./spend/MonthlyBarChart";
import { SegmentedControl } from "./spend/SegmentedControl";
import { SpendBreakdown } from "./spend/SpendBreakdown";

// J4 — spend reporting, and the screen where brief §9b becomes visible.
//
// The rule: AGGREGATES follow the viewer's REPORTING scope; ITEMS follow their
// ORDER scope. So a figure can be readable and not openable. Switch to the
// Finance persona to see it — organisation-wide reporting, own-orders-only
// visibility, so Bristol's total is shown but not drillable.
//
// Invoiced and ordered are shown side by side, never summed: a customer
// reconciling against their own ledger must be able to see which number is
// which (brief §6). Invoiced leads, because it is the figure that matches an
// invoice; ordered sits beside it as work still to be billed. The monthly chart
// and the breakdowns below are the one place the two bases are counted
// together, which the page says in words where it happens.
//
// A capability the caller lacks is a 403, never an empty report (05_ §11
// invariant 6) — hiding the nav item is not enough, a deep link would
// otherwise render £0 and empty breakdowns, which reads as "you have never
// bought anything" rather than "this is not switched on for you" (brief
// §11). This page calls getSpendReport() unconditionally and reads the
// ApiError status to tell the two apart, rather than pre-checking the
// session's capabilities client-side — the server's denial is the gate.

export function PortalSpendPage() {
  const adapter = useCustomerDataAdapter();
  const { viewKey, buildPortalUrl } = usePortalViewer();
  const [period, setPeriod] = useState<SpendPeriodId>("12m");
  const fromDate = useMemo(
    () => spendPeriodFromDate(period, currentTime()),
    [period],
  );

  const { state, retry } = useListQuery(
    () => adapter.getSpendReport({ fromDate }),
    [adapter, viewKey, fromDate],
  );

  if (state.status === "loading") {
    return (
      <Card
        role="status"
        className="mx-auto max-w-md p-8 text-center text-sm text-muted-foreground"
      >
        Loading your spend…
      </Card>
    );
  }

  if (state.status === "error") {
    if (state.error instanceof ApiError && state.error.status === 403) {
      return (
        <Card className="mx-auto max-w-md space-y-3 p-8 text-center">
          <Lock className="mx-auto size-6 text-muted-foreground" />
          <p className="font-medium">
            Spend reporting isn&apos;t switched on for you
          </p>
          <p className="text-sm text-muted-foreground">
            Someone at your organisation with administrator access can turn it
            on, or ask us and we&apos;ll set it up.
          </p>
          <div>
            <Button asChild variant="outline">
              <Link to={buildPortalUrl(paths.portalOrders())}>
                Back to orders
              </Link>
            </Button>
          </div>
        </Card>
      );
    }
    return (
      <Card className="mx-auto max-w-md space-y-3 p-8 text-center">
        <AlertTriangle className="mx-auto size-6 text-muted-foreground" />
        <p className="font-medium">We couldn&apos;t load your spend</p>
        <p className="text-sm text-muted-foreground">{state.message}</p>
        <Button variant="outline" onClick={retry}>
          Try again
        </Button>
      </Card>
    );
  }

  return (
    <SpendReport
      report={state.data}
      period={period}
      fromDate={fromDate}
      onPeriodChange={setPeriod}
      buildPortalUrl={buildPortalUrl}
    />
  );
}

function SpendReport({
  report,
  period,
  fromDate,
  onPeriodChange,
  buildPortalUrl,
}: {
  report: CustomerSpendReport;
  period: SpendPeriodId;
  fromDate: string;
  onPeriodChange: (p: SpendPeriodId) => void;
  buildPortalUrl: (path: string) => To;
}) {
  const { sessionState } = usePortalViewer();
  const capabilities = usePlatformCapabilities();

  const invoiced = Number(report.invoiced_total);
  const committed = Number(report.committed_total);
  const chart = useMemo(() => shapeChart(report.series), [report.series]);

  // Nothing at all in the period: say so, rather than headline GBP 0 totals
  // that read as "you have never bought anything".
  const hasNoSpend =
    invoiced === 0 &&
    committed === 0 &&
    report.series.length === 0 &&
    report.by_site.length === 0;

  function exportCsv() {
    capabilities.downloadTextFile(
      `pollards-spend-${period}-${currentTime().toISOString().slice(0, 10)}.csv`,
      spendReportCsv(report),
      "text/csv",
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Spend</h1>
          <p className="text-sm text-muted-foreground">
            Everything you have bought from us, however it was ordered.
          </p>
        </div>
        {sessionState.status === "success" &&
          sessionState.data.can_see_spend && (
            <PortalScopeNote
              visibility={sessionState.data.visibility}
              kind="spend"
              customerName={sessionState.data.customer_name}
            />
          )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <SegmentedControl
          label="Period"
          value={period}
          onChange={onPeriodChange}
          options={SPEND_PERIODS.map((p) => ({ id: p.id, label: p.label }))}
          className="w-full sm:w-auto"
        />
        <p className="text-sm text-muted-foreground">
          {formatSpendDate(fromDate)} to today
        </p>
        <div className="flex-1" />
        {!hasNoSpend && (
          <Button variant="outline" onClick={exportCsv}>
            <Download className="size-4" />
            Export
          </Button>
        )}
      </div>

      {hasNoSpend ? (
        <Card className="mx-auto max-w-md space-y-1 p-8 text-center">
          <p className="font-medium">No orders in this period</p>
          <p className="text-sm text-muted-foreground">
            Try a longer period to see earlier spend.
          </p>
        </Card>
      ) : (
        <>
          <section aria-labelledby="spend-totals-heading">
            <Card className="gap-0 p-0">
              <h2 id="spend-totals-heading" className="sr-only">
                Totals for this period
              </h2>
              <div className="grid sm:grid-cols-[3fr_2fr]">
                <div className="p-5 sm:p-6">
                  <p className="text-sm font-medium text-muted-foreground">
                    Invoiced to you
                  </p>
                  <p className="mt-1 text-4xl font-semibold tabular-nums tracking-tight">
                    {formatCurrencyGbp(invoiced)}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Orders placed in this period that we&apos;ve invoiced.
                  </p>
                </div>
                <div className="border-t p-5 sm:border-l sm:border-t-0 sm:p-6">
                  <p className="text-sm font-medium text-muted-foreground">
                    Ordered
                  </p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-muted-foreground">
                    {formatCurrencyGbp(committed)}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Orders placed in this period that we haven&apos;t invoiced
                    yet.
                  </p>
                </div>
              </div>
              <p className="border-t bg-muted/40 px-5 py-3 text-xs text-muted-foreground sm:px-6">
                Kept apart on purpose: invoiced is what appears on your
                invoices, ordered is work still to be billed. We don&apos;t add
                them together.
              </p>
            </Card>
          </section>

          {chart.bars.length > 1 && (
            <section aria-labelledby="spend-months-heading">
              <Card className="gap-1 p-4 sm:p-5">
                <h2 id="spend-months-heading" className="font-medium">
                  By month
                </h2>
                <p className="mb-2 text-sm text-muted-foreground">
                  Orders placed each month, invoiced or not.
                </p>
                <MonthlyBarChart chart={chart} label="Orders placed by month" />
              </Card>
            </section>
          )}

          <SpendBreakdown report={report} buildPortalUrl={buildPortalUrl} />
        </>
      )}
    </div>
  );
}
